import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import './add_patient.css';
import { AddPatientMobileStep1 } from "./add_patient_mobile";
import { API_BASE_URL } from '../config.js';

export default function AddPatient() {
  const navigate = useNavigate();
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  
  // 🔹 توحيد الحالة (Single State) لجميع الحقول
  const [formData, setFormData] = useState({
    name: "",
    birth_date: null,
    national_number: "",
    phone: "",
    gender: "",
  });

  const [errors, setErrors] = useState({});
  const [statusMessage, setStatusMessage] = useState({ type: "", text: "" }); // 🔹 توحيد رسائل الحالة
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // 🛡️ حماية وتصفية المدخلات
  const sanitizeInput = (value) => {
    if (typeof value !== 'string') return value;
    return value.replace(/[<>"']/g, "");
  };

  const handleChange = (e) => {
    // دعم الـ DatePicker الذي يرسل التاريخ مباشرة، أو الـ Inputs العادية
    const name = e.target ? e.target.name : 'birth_date';
    const value = e.target ? e.target.value : e;

    let cleanValue = sanitizeInput(value);

    // حماية: الأرقام فقط للهاتف والرقم الوطني
    if ((name === "national_number" || name === "phone") && cleanValue && !/^\d+$/.test(cleanValue)) {
      return; 
    }

    setFormData(prev => ({ ...prev, [name]: cleanValue }));
    if (errors[name]) setErrors(prev => ({ ...prev, [name]: null }));
    setStatusMessage({ type: "", text: "" }); // مسح الأخطاء السابقة
  };

  const validate = () => {
    let newErrors = {};
    if (!formData.name) newErrors.name = "الاسم الثلاثي مطلوب";
    if (!formData.national_number || formData.national_number.length !== 11) newErrors.national_number = "الرقم الوطني يجب أن يكون 11 رقماً";
    if (!formData.gender) newErrors.gender = "يرجى تحديد الجنس";
    if (!formData.phone) newErrors.phone = "رقم الهاتف مطلوب";
    if (!formData.birth_date) newErrors.birth_date = "تاريخ الميلاد مطلوب";

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // 🔹 دالة موحدة للموبايل والحاسوب (تفحص السيرفر ثم تنتقل)
  const handleProceed = async (e) => {
    if (e && e.preventDefault) e.preventDefault();
    if (!validate()) {
      setStatusMessage({ type: "error", text: "يرجى التأكد من تعبئة جميع الحقول المطلوبة بشكل صحيح." });
      return;
    }

    setIsSubmitting(true);
    setStatusMessage({ type: "loading", text: "جاري التحقق من بيانات المريض..." });

    try {
      const token = localStorage.getItem("access_token");
      
      const response = await fetch(`${API_BASE_URL}/patients/?national_number=${formData.national_number}`, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          ...(token ? { "Authorization": `Bearer ${token}` } : {})
        }
      });

      if (response.status === 401) throw new Error("EXPIRED_SESSION");
      
      if (response.ok) {
        const data = await response.json();
        if (data.results && data.results.length > 0) {
          throw new Error("EXISTS");
        }
      } else {
        throw new Error("SERVER_ERROR");
      }

      // تجهيز التاريخ للخطوة الثانية
      const formattedDate = formData.birth_date ? new Date(formData.birth_date).toISOString().split('T')[0] : null;
      
      setStatusMessage({ type: "success", text: "البيانات سليمة، جاري الانتقال..." });
      
      setTimeout(() => {
        navigate("/add-patient-step2", { 
          state: { ...formData, birth_date: formattedDate } 
        });
      }, 500);

    } catch (error) {
      setIsSubmitting(false);
      switch(error.message) {
        case "EXPIRED_SESSION":
          setStatusMessage({ type: "error", text: "انتهت الجلسة. يرجى تسجيل الدخول مجدداً." });
          break;
        case "EXISTS":
          setStatusMessage({ type: "error", text: "يوجد مريض مسجل مسبقاً بهذا الرقم الوطني!" });
          break;
        case "Failed to fetch":
          setStatusMessage({ type: "error", text: "لا يوجد اتصال بالسيرفر. تأكد من الإنترنت." });
          break;
        default:
          setStatusMessage({ type: "error", text: "حدث خطأ غير متوقع. يرجى المحاولة لاحقاً." });
      }
    }
  };

  if (isMobile) {
    return (
      <AddPatientMobileStep1
        formData={formData}
        errors={errors}
        handleChange={handleChange}
        onNext={handleProceed}
        isSubmitting={isSubmitting}
        statusMessage={statusMessage}
      />
    );
  }

  // 💻 واجهة الحاسوب
  return (
    <div className="add-patient-container" dir="rtl">
      <div className="add-patient-left">
        <form className="add-patient-form" onSubmit={handleProceed}>
          <h3 className="add-patient-h3">إضافة مريض جديد</h3>

          {/* استخدام واجهة الأخطاء الموحدة */}
          {statusMessage.text && (
            <div className={`status-alert alert-${statusMessage.type}`} style={{ marginBottom: '15px' }}>
              {statusMessage.type === 'loading' && <i className="fa-solid fa-circle-notch fa-spin"></i>}
              {statusMessage.type === 'error' && <i className="fa-solid fa-triangle-exclamation"></i>}
              {statusMessage.type === 'success' && <i className="fa-solid fa-circle-check"></i>}
              <span style={{marginLeft: '10px'}}>{statusMessage.text}</span>
            </div>
          )}

          <div className="add-patient-input-group">
            <i className="fa-solid fa-user"></i>
            <input name="name" placeholder="اسم المريض" value={formData.name} onChange={handleChange} disabled={isSubmitting}/>
          </div>

          <div className="add-patient-input-group">
            <i className="fa-solid fa-calendar"></i>
            <DatePicker 
              selected={formData.birth_date} 
              onChange={handleChange}
              placeholderText="تاريخ الميلاد" 
              dateFormat="yyyy-MM-dd"
              disabled={isSubmitting}
            />
          </div>

          <div className="add-patient-input-group">
            <i className="fa-solid fa-id-card"></i>
            <input name="national_number" placeholder="الرقم الوطني" value={formData.national_number} onChange={handleChange} disabled={isSubmitting} maxLength="11"/>
          </div>

          <div className="add-patient-input-group">
            <i className="fa-solid fa-phone"></i>
            <input name="phone" placeholder="رقم الهاتف" value={formData.phone} onChange={handleChange} disabled={isSubmitting} />
          </div>

          <h6 className="gender-add-patient">الجنس</h6>
          <div className="gender-group">
            <label className="radio-option">
              <input type="radio" name="gender" value="male" checked={formData.gender === 'male'} onChange={handleChange} disabled={isSubmitting}/> ذكر
            </label>
            <label className="radio-option">
              <input type="radio" name="gender" value="female" checked={formData.gender === 'female'} onChange={handleChange} disabled={isSubmitting}/> أنثى
            </label>
          </div>
          
          <button 
            type="submit" 
            className="add-patient-submit" 
            disabled={isSubmitting}
            style={{ opacity: isSubmitting ? 0.7 : 1, cursor: isSubmitting ? 'wait' : 'pointer' }}
          > 
            {isSubmitting ? 'جاري التحقق...' : 'متابعة'}
          </button>
        </form>
      </div>

      <div className="add-patient-right">
        <img className='syria-logo-add-patient-logo' src="/images/Emblem_of_Syria_(2025–present).svg.png" alt="Syria Emblem" />
        <h1 className="add-patient-title">الجمهورية العربية السورية</h1>
        <h4 className="add-patient-sub">وزارة الصحة - منصة التنبؤ بالأوبئة</h4>
      </div>
    </div>
  );
}   